import clsx from "clsx";
import Container from "./Container";
import Cta from "./CallToAction";
import RichText from "./RichText";

export default function StandardSliceLayout({
  title,
  introTitle,
  introTitleSize = "large",
  introText,
  backgroundIsBlack = false,
  introCtaLink,
  introCtaText,
  children,
  largeContent,
}: {
  title?: string | null;
  introTitle?: string | null;
  introTitleSize?: "xlarge" | "large" | "medium" | "small";
  introText?: string | null;
  backgroundIsBlack?: boolean;
  introCtaLink?: string | null;
  introCtaText?: string | null;
  children?: React.ReactNode;
  largeContent?: boolean;
}) {
  //This component is a base-layout for most of the slices. It contains a slice
  //title on the left, and on the right the main content of the slice. This main
  //content often contains a heading and/or intro text, so we also put that in
  //this component to standardize. The rest of the sections content can be added
  //as children and will automatically be aligned with the heading/intro text

  //We want this section to be able to use a rich-text field, but also a string
  //if necesary, so we handle that here. The other inputs are converted to
  //asText beforehand, but we want the possibility of the richtext for the intro
  //field, so we have to handle that in this component.
  function renderIntroText() {
    if (!introText) return null;
    return <RichText field={introText} />;
  }

  //Because we don't know if this is a string or richtext, we need to explicitly
  //check
  const hasOnlyChildren = !introText && !introTitle;

  return (
    <Container
      containerClass="grid gap-6 md:grid-cols-8 lg:grid-cols-12 md:gap-8"
      anchorName={title ? title : null}
    >
      {/* Title on the left (always render outer div for the grid layout) */}
      {title && (
        <div
          className={clsx(
            "md:col-span-8",
            !title && "hidden lg:block",
            largeContent ? "lg:col-span-3" : "lg:col-span-4",
          )}
        >
          <h2
            className={clsx(
              "text-heading-6",
              backgroundIsBlack ? "text-white/70" : "text-black/60",
            )}
          >
            {title}
          </h2>
        </div>
      )}
      {!hasOnlyChildren && (
        <div className="md:col-span-8 lg:col-start-5 xl:col-start-5">
          {/* We don't want to render this if there is no content because of the margin */}
          {(introTitle ?? introText) && (
            <div className="grid gap-y-3 md:grid-cols-8">
              {/* The main content of the slice, the start of which can contain a title and description which are always styled the same */}
              {introTitle && (
                <SubHeading
                  className={clsx(
                    "text-heading-5",
                    backgroundIsBlack ? "text-white" : "text-black",
                    introTitleSize === "xlarge" && "md:col-span-7",
                    introTitleSize === "large" && "md:col-span-7 xl:col-span-7",
                    introTitleSize === "medium" &&
                      "md:col-span-7 xl:col-span-6",
                    introTitleSize === "small" && "md:col-span-6 xl:col-span-5",
                  )}
                  hasTitle={!!title}
                >
                  {introTitle}
                </SubHeading>
              )}
              {/* Check if we have any content */}
              {introText && (
                <div
                  className={clsx(
                    "text-body md:col-span-7",
                    backgroundIsBlack ? "text-white/80" : "text-black/80",
                  )}
                >
                  {renderIntroText()}
                </div>
              )}
              {introCtaText && introCtaLink && (
                <div className="mt-3 md:col-span-8">
                  <Cta to={introCtaLink} isOnDarkBackground={backgroundIsBlack}>
                    {introCtaText}
                  </Cta>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {children && (
        // The children should only be used if they fall exactly in the 'normal
        // flow' (1 column offset on md and then 4 on lg+). Otherwise, render
        // the content separately and handle the layout there.
        <div
          className={clsx(
            "relative md:col-span-7 md:col-start-2 lg:col-span-8 lg:col-start-5",
          )}
        >
          {children}
        </div>
      )}
    </Container>
  );
}

function SubHeading({
  className,
  hasTitle,
  children,
}: {
  className: string;
  hasTitle: boolean;
  children: React.ReactNode;
}) {
  if (hasTitle) return <h3 className={className}>{children}</h3>;
  return <h2 className={className}>{children}</h2>;
}
